import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutCandidate } from 'actions';
import { clearCheckrProfileAuthSession } from 'actions/candidateLogin';
import { workosLogout } from 'actions/workosLogin';
import { isInternational } from 'lib/helpers';
import { PATHS } from '../constants';
import { parseJwtExp } from './parseJwtExp';

/**
 * Watches `portal.token` from Redux, computes time until JWT expiry,
 * and triggers a full logout + redirect when the token expires.
 *
 * Each token change cancels the old timer and starts a new one.
 * If the token is already expired when the hook runs, logout is immediate.
 *
 * // TODO: Add a "session expiring" warning modal 2-3 min before expiry
 */
const useSessionTimeout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const portalToken = useSelector((state: any) => state.portal?.token);

  useEffect(() => {
    if (!portalToken) return undefined;

    const exp = parseJwtExp(portalToken);
    if (exp === null) return undefined;

    const msUntilExpiry = exp * 1000 - Date.now();

    const performLogout = async () => {
      try {
        await clearCheckrProfileAuthSession();
      } catch {
        // Server session cleanup is best-effort
      }
      try {
        await dispatch(workosLogout());
      } catch {
        // Server session cleanup is best-effort
      }
      await dispatch(logoutCandidate());
      const logoutPath = isInternational() ? PATHS.INTERNATIONAL : '/';
      navigate(logoutPath);
    };

    if (msUntilExpiry <= 0) {
      performLogout();
      return undefined;
    }

    const timer = setTimeout(performLogout, msUntilExpiry);
    return () => clearTimeout(timer);
  }, [portalToken, dispatch, navigate]);
};

export default useSessionTimeout;
